import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, Plus, Mail, Download } from "lucide-react";
import { api, fmtINR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Invoices() {
  const [rows, setRows] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [open, setOpen] = useState(false);
  const [bookingId, setBookingId] = useState("");
  const [gst, setGst] = useState("18");
  const [extraDesc, setExtraDesc] = useState("");
  const [extraAmt, setExtraAmt] = useState("");
  const [sendMail, setSendMail] = useState(true);

  const load = () => {
    api.get("/admin/invoices").then(({ data }) => setRows(data)).catch(() => {});
    api.get("/admin/bookings").then(({ data }) => setBookings(data.filter((b) => b.status !== "cancelled"))).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const downloadPdf = async (inv) => {
    try {
      const res = await api.get(`/admin/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${inv.invoice_number}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("PDF download failed"); }
  };

  const generate = async () => {
    if (!bookingId) return toast.error("Select a booking");
    const extra = extraDesc && extraAmt ? [{ description: extraDesc, amount: Number(extraAmt) }] : [];
    try {
      await api.post("/admin/invoices", { booking_id: bookingId, gst_percent: Number(gst), extra_items: extra, send_email: sendMail });
      toast.success("Invoice generated" + (sendMail ? " & emailed" : ""));
      setOpen(false); setBookingId(""); setExtraDesc(""); setExtraAmt(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl text-zinc-900">Invoices (GST)</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="gen-invoice-btn" className="gold-gradient text-white font-semibold"><Plus size={16} className="mr-1" /> Generate Invoice</Button></DialogTrigger>
          <DialogContent className="bg-card border-pink-200 text-zinc-900">
            <DialogHeader><DialogTitle className="font-display text-xl">Generate GST Invoice</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-zinc-700">Booking</Label>
                <Select value={bookingId} onValueChange={setBookingId}>
                  <SelectTrigger data-testid="invoice-booking-select" className="mt-1 bg-white border-pink-200 text-zinc-900"><SelectValue placeholder="Select booking" /></SelectTrigger>
                  <SelectContent>{bookings.map((b) => <SelectItem key={b.id} value={b.id}>{b.booking_number} · {b.customer_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-700">GST %</Label>
                <Input type="number" value={gst} data-testid="invoice-gst" onChange={(e) => setGst(e.target.value)} className="mt-1 bg-white border-pink-200 text-zinc-900" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-zinc-700">Extra item</Label><Input value={extraDesc} data-testid="invoice-extra-desc" onChange={(e) => setExtraDesc(e.target.value)} placeholder="Add-on" className="mt-1 bg-white border-pink-200 text-zinc-900" /></div>
                <div><Label className="text-zinc-700">Amount (₹)</Label><Input type="number" value={extraAmt} data-testid="invoice-extra-amt" onChange={(e) => setExtraAmt(e.target.value)} className="mt-1 bg-white border-pink-200 text-zinc-900" /></div>
              </div>
              <label className="flex items-center gap-2 text-sm text-zinc-700">
                <Checkbox checked={sendMail} onCheckedChange={setSendMail} data-testid="invoice-sendmail" /> Email invoice to customer
              </label>
              <Button onClick={generate} data-testid="save-invoice-btn" className="w-full gold-gradient text-white font-semibold rounded-full">Generate</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-card border border-pink-200 rounded-xl overflow-hidden overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-pink-50 text-zinc-600 text-left"><tr>{["Invoice #", "Booking", "Customer", "Subtotal", "GST", "Total", "Balance", ""].map((h) => <th key={h} className="px-4 py-3 font-medium whitespace-nowrap">{h}</th>)}</tr></thead>
          <tbody>
            {rows.map((inv) => (
              <tr key={inv.id} className="border-t border-pink-100" data-testid={`invoice-row-${inv.invoice_number}`}>
                <td className="px-4 py-3 text-gold font-mono flex items-center gap-2"><FileText size={14} /> {inv.invoice_number}</td>
                <td className="px-4 py-3 text-zinc-700">{inv.booking_number}</td>
                <td className="px-4 py-3 text-zinc-900">{inv.customer_name}</td>
                <td className="px-4 py-3 text-zinc-700">{fmtINR(inv.subtotal)}</td>
                <td className="px-4 py-3 text-zinc-700">{inv.gst_percent}% · {fmtINR(inv.gst_amount)}</td>
                <td className="px-4 py-3 text-zinc-900 font-medium">{fmtINR(inv.total)}</td>
                <td className="px-4 py-3 text-amber-400">{fmtINR(inv.balance_due)}</td>
                <td className="px-4 py-3"><button onClick={() => downloadPdf(inv)} data-testid={`invoice-pdf-${inv.invoice_number}`} className="flex items-center gap-1 text-gold hover:underline text-sm"><Download size={15} /> PDF</button></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-zinc-500">No invoices generated</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
